import { Box, Caption, IconButton, NavArrowRight, TextArea } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { isDesktop } from "react-device-detect";
import { useTranslation } from "react-i18next";

import style from "./ai-query-input.scss";
import disclaimerStyle from "components/ai-panel/ai-disclaimer/ai-disclaimer.scss";
import AIDisclaimer from "components/ai-panel/ai-disclaimer/AIDisclaimer";
import { roundTo } from "utils/format";

interface Props {
    onSubmit: (message: string) => void;
    disabled?: boolean;
    isSubmitting?: boolean;
    placeholder: string;
    customTrigger?: React.ReactNode;
    queryInputTestId?: string;
    submitButtonTestId?: string;
    className?: string;
    hideDisclaimer?: boolean;
    hideLicenseConsumptionCaption?: boolean;
    consumedLicenseCount?: number;
    fullWidthOnDesktop?: boolean;
}

const MAX_TEXTAREA_HEIGHT_PIXELS = 100;

export const AIQueryInput = (props: Props) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = React.useState("");
    const [isMultiline, setIsMultiline] = React.useState(false);
    const inputAreaRef = React.useRef<HTMLDivElement>(null);
    const resizeAnimationFrameRef = React.useRef<number | undefined>(undefined);
    const runTextareaTransition = (
        textarea: HTMLTextAreaElement,
        currentPaddingLeft: string,
        currentPaddingRight: string
    ) => {
        textarea.style.paddingLeft = currentPaddingLeft;
        textarea.style.paddingRight = currentPaddingRight;
        textarea.style.transition = "";

        if (resizeAnimationFrameRef.current !== undefined) {
            cancelAnimationFrame(resizeAnimationFrameRef.current);
        }

        resizeAnimationFrameRef.current = requestAnimationFrame(() => {
            resizeAnimationFrameRef.current = undefined;
            textarea.style.paddingLeft = "";
            textarea.style.paddingRight = "";
        });
    };

    const resizeTextarea = () => {
        const inputArea = inputAreaRef.current;
        const textarea = inputArea?.querySelector("textarea");
        const inputWrapper = textarea?.parentElement;
        if (!inputArea || !textarea || !inputWrapper) {
            return;
        }

        const computedStyle = getComputedStyle(inputArea);

        const singleLinePaddingLeft = computedStyle.getPropertyValue("--single-line-padding-left");
        const multilinePaddingLeft = computedStyle.getPropertyValue("--multiline-padding-left");
        const singleLinePaddingRight = computedStyle.getPropertyValue("--single-line-padding-right");
        const multilinePaddingRight = computedStyle.getPropertyValue("--multiline-padding-right");
        const singleLineTextareaHeightPixels = parseFloat(
            computedStyle.getPropertyValue("--single-line-textarea-height")
        );

        // Disable transitions and force single-line padding before measuring isMultiline state to avoid a rendering loop and a buggy transition.
        // When switching to multiline, the smaller multiline padding causes the text to fit on one line again, resetting the state back to single-line
        // which re-wraps the text and so on.
        textarea.style.transition = "none";
        textarea.style.paddingLeft = singleLinePaddingLeft;
        textarea.style.paddingRight = singleLinePaddingRight;
        inputWrapper.style.height = "1px";

        const newIsMultiline = textarea.scrollHeight > singleLineTextareaHeightPixels;

        if (newIsMultiline) {
            textarea.style.paddingLeft = multilinePaddingLeft;
            textarea.style.paddingRight = multilinePaddingRight;
        }

        inputWrapper.style.height = `${Math.max(singleLineTextareaHeightPixels, Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PIXELS))}px`;

        const currentPaddingLeft = isMultiline ? multilinePaddingLeft : singleLinePaddingLeft;
        const currentPaddingRight = isMultiline ? multilinePaddingRight : singleLinePaddingRight;

        runTextareaTransition(textarea, currentPaddingLeft, currentPaddingRight);
        setIsMultiline(newIsMultiline);
    };

    const resetInput = () => {
        setInputValue("");

        const textarea = inputAreaRef.current?.querySelector("textarea");
        const inputWrapper = textarea?.parentElement;

        if (resizeAnimationFrameRef.current !== undefined) {
            cancelAnimationFrame(resizeAnimationFrameRef.current);
            resizeAnimationFrameRef.current = undefined;
        }

        if (textarea) {
            textarea.style.paddingLeft = "";
        }

        if (inputWrapper) {
            const singleLineTextareaHeightPixels = parseFloat(
                getComputedStyle(inputWrapper).getPropertyValue("--single-line-textarea-height")
            );
            inputWrapper.style.height = `${singleLineTextareaHeightPixels}px`;
        }

        setIsMultiline(false);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedMessage = inputValue.trim();
        if (!trimmedMessage || props.disabled || props.isSubmitting) {
            return;
        }
        props.onSubmit(trimmedMessage);
        resetInput();
    };

    const content = (
        <>
            {!props.hideDisclaimer && !props.hideLicenseConsumptionCaption && (
                <Box horizontalAlignment="Right" className={style.consumptionCaptionWrapper}>
                    <Caption greyTextStyle className={classNames(disclaimerStyle.disclaimer, style.consumptionCaption)}>
                        {t("AiAgent.licenseConsumptionCaption", {
                            consumptionValue:
                                props.consumedLicenseCount !== undefined
                                    ? roundTo(props.consumedLicenseCount, 2)
                                    : undefined,
                        })}
                    </Caption>
                </Box>
            )}
            <form
                onSubmit={handleSubmit}
                className={classNames(
                    style.queryInput,
                    {
                        [style.hasDisclaimer]: !props.hideDisclaimer,
                        [style.fullWidthOnDesktop]: props.fullWidthOnDesktop,
                    },
                    props.className
                )}
            >
                <div
                    className={classNames(style.inputArea, {
                        multiline: isMultiline,
                    })}
                    ref={inputAreaRef}
                >
                    <div className={style.textareaWrapper}>
                        <TextArea
                            name="aiQuery"
                            placeholder={props.placeholder}
                            autoFocus={isDesktop}
                            hideLabelSection
                            disabled={props.disabled}
                            value={inputValue}
                            onChange={(event) => {
                                setInputValue(event.target.value);
                                resizeTextarea();
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    event.currentTarget.form?.requestSubmit();
                                }
                            }}
                            testIds={{ textArea: props.queryInputTestId }}
                        />
                    </div>

                    <div className={style.iconButtonRow}>
                        {props.customTrigger ?? (
                            <IconButton
                                testId={props.submitButtonTestId}
                                className={style.sendButton}
                                type="submit"
                                disabled={props.disabled || props.isSubmitting || inputValue.trim() === ""}
                            >
                                <NavArrowRight />
                            </IconButton>
                        )}
                    </div>
                </div>
            </form>
            {!props.hideDisclaimer && (
                <Box horizontalAlignment="Center" className={style.disclaimerWrapper}>
                    <AIDisclaimer />
                </Box>
            )}
        </>
    );

    if (props.hideDisclaimer) {
        return content;
    }

    return (
        <Box columnLayout className={style.fullWidthColumn}>
            {content}
        </Box>
    );
};
